/**
 * RoleEditor - Colonne droite : éditeur d'un rôle.
 *
 * Conteneur d'onglets (Affichage / Permissions / Membres). Charge le détail
 * du rôle via GET /api/roles/:id et passe les données aux sous-onglets.
 */

import { useState, useEffect, useCallback } from 'react';
import { ChevronLeftIcon } from '../../icons';
import { RoleDetail, DEFAULT_ROLE_COLOR, API_BASE, authHeaders } from './types';
import DisplayTab from './DisplayTab';
import PermissionsTab from './PermissionsTab';
import MembersTab from './MembersTab';

interface RoleEditorProps {
  roleId: string | null;
  onBack?: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}

type Tab = 'display' | 'permissions' | 'members';

function RoleEditor({ roleId, onBack, onChanged, onDeleted }: RoleEditorProps) {
  const [role, setRole] = useState<RoleDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('display');

  const fetchRole = useCallback(async () => {
    if (!roleId) {
      setRole(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      console.log(`[RoleEditor] Fetching /api/roles/${roleId}`);
      const res = await fetch(`${API_BASE}/api/roles/${roleId}`, {
        headers: authHeaders()
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json?.error?.message || `Erreur HTTP ${res.status}`);
      }
      setRole(json.role);
      console.log(`[RoleEditor] Détail chargé pour ${json.role?.name}`);
    } catch (err) {
      console.error('[RoleEditor] Erreur fetch:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    fetchRole();
    // Reset onglet sur changement de rôle
    setActiveTab('display');
  }, [fetchRole]);

  // État vide
  if (!roleId) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '48px 24px',
        textAlign: 'center',
        color: '#94a3b8',
        fontSize: '14px',
        minHeight: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        Sélectionnez un rôle pour le modifier ou créez-en un nouveau.
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '48px',
        textAlign: 'center',
        color: '#94a3b8'
      }}>
        Chargement...
      </div>
    );
  }

  if (error || !role) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '24px'
      }}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '16px',
              padding: '6px 12px',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            <ChevronLeftIcon size={14} />
            Retour
          </button>
        )}
        <div style={{
          padding: '12px',
          backgroundColor: '#fef2f2',
          color: '#991b1b',
          border: '1px solid #fca5a5',
          borderRadius: '6px',
          fontSize: '13px'
        }}>
          {error || 'Rôle introuvable.'}
        </div>
      </div>
    );
  }

  const handleSaved = () => {
    fetchRole();
    onChanged();
  };

  const handleDeleted = () => {
    onDeleted();
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'display', label: 'Affichage' },
    { id: 'permissions', label: 'Permissions' },
    { id: 'members', label: `Membres (${role.member_count})` }
  ];

  const safeColor = role.color || DEFAULT_ROLE_COLOR;

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              alignSelf: 'flex-start',
              padding: '6px 12px',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            <ChevronLeftIcon size={14} />
            Retour
          </button>
        )}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: safeColor,
            flexShrink: 0
          }} />
          <div style={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#1e293b'
          }}>
            {role.display_name}
          </div>
          {role.is_system && (
            <span style={{
              fontSize: '11px',
              padding: '2px 8px',
              backgroundColor: '#fef2f2',
              color: '#991b1b',
              borderRadius: '4px',
              fontWeight: 600,
              border: '1px solid #fca5a5',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Système
            </span>
          )}
          {role.is_default && (
            <span style={{
              fontSize: '11px',
              padding: '2px 8px',
              backgroundColor: '#dbeafe',
              color: '#1e40af',
              borderRadius: '4px',
              fontWeight: 600,
              border: '1px solid #93c5fd'
            }}>
              par défaut
            </span>
          )}
        </div>
        <div style={{ fontSize: '12px', color: '#94a3b8' }}>
          Identifiant technique : <code style={{
            backgroundColor: '#f1f5f9',
            padding: '1px 6px',
            borderRadius: '4px',
            fontFamily: 'monospace'
          }}>{role.name}</code>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #e2e8f0',
        backgroundColor: '#f8fafc',
        overflowX: 'auto'
      }}>
        {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 20px',
                background: 'transparent',
                border: 'none',
                borderBottom: isActive ? '2px solid #3b82f6' : '2px solid transparent',
                color: isActive ? '#3b82f6' : '#475569',
                fontSize: '14px',
                fontWeight: isActive ? 600 : 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Contenu de l'onglet */}
      <div style={{ padding: '20px' }}>
        {activeTab === 'display' && (
          <DisplayTab role={role} onSaved={handleSaved} onDeleted={handleDeleted} />
        )}
        {activeTab === 'permissions' && (
          <PermissionsTab role={role} onSaved={handleSaved} />
        )}
        {activeTab === 'members' && (
          <MembersTab role={role} onChanged={handleSaved} />
        )}
      </div>
    </div>
  );
}

export default RoleEditor;
